import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { API, formatApiError, useAuth } from "../AuthContext";
import AppHeader from "../components/AppHeader";
import SiteFooter from "../components/SiteFooter";

export default function AppHome() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [thought, setThought] = useState("");
  const [reframe, setReframe] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [usage, setUsage] = useState(null);
  const [journalCount, setJournalCount] = useState(0);
  const textareaRef = useRef(null);

  useEffect(() => {
    fetchUsage();
    fetchJournalCount();
  }, []);

  const fetchUsage = async () => {
    try {
      const { data } = await axios.get(`${API}/usage`);
      setUsage(data);
    } catch {}
  };

  const fetchJournalCount = async () => {
    try {
      const { data } = await axios.get(`${API}/journal`);
      setJournalCount(data.entries?.length || 0);
    } catch {}
  };

  const handleReframe = async () => {
    if (!thought.trim() || loading) return;
    setLoading(true);
    setError(null);
    setReframe(null);
    try {
      const { data } = await axios.post(`${API}/reframe`, { thought: thought.trim() });
      setReframe(data.entry);
      fetchUsage();
      setJournalCount((c) => c + 1);
    } catch (err) {
      if (err.response?.status === 402) {
        setError("paywall");
      } else {
        setError(formatApiError(err.response?.data?.detail, "Couldn't reach the reframe. Try again in a moment."));
      }
    } finally {
      setLoading(false);
    }
  };

  const startOver = () => {
    setReframe(null);
    setThought("");
    setError(null);
    setTimeout(() => textareaRef.current?.focus(), 100);
  };

  const showPaywall = error === "paywall";
  const isSubscribed = usage?.is_subscribed;

  return (
    <div className="page" data-testid="app-home">
      <div className="grain-bg" />
      <div className="container-narrow">
        <AppHeader showJournalCount journalCount={journalCount} />

        {/* Subscription / usage banner */}
        {!isSubscribed && usage && !reframe && (
          <div className="banner" data-testid="usage-banner">
            <span>
              {usage.used_today >= 1
                ? "You've used your free reframe today."
                : "1 free reframe today · Unlimited with membership."}
            </span>
            <button className="banner-cta" onClick={() => navigate("/upgrade")} data-testid="banner-upgrade-btn">
              Upgrade →
            </button>
          </div>
        )}
        {isSubscribed && !reframe && (
          <div className="banner" data-testid="member-banner" style={{ borderColor: "var(--ink)" }}>
            <span>
              Member · Unlimited reframes <span className="pro-badge">Pro</span>
            </span>
            <Link to="/upgrade" className="banner-cta" data-testid="banner-manage-btn">Manage</Link>
          </div>
        )}

        {/* INPUT VIEW */}
        {!reframe && !showPaywall && (
          <div className="fade-in">
            <div className="eyebrow" style={{ marginBottom: 20 }}>The Daily Reframe</div>
            <h1 className="display" data-testid="prompt-heading">
              What's looping<br />in your head today?
            </h1>

            <textarea
              ref={textareaRef}
              value={thought}
              onChange={(e) => setThought(e.target.value)}
              placeholder="Write the thought here — anything heavy, anything tight. Then hit Reframe."
              rows={5}
              className="thought-frame"
              style={{
                marginBottom: 14,
              }}
              data-testid="thought-input"
              disabled={loading}
            />

            <div
              data-testid="home-privacy-note"
              className="serif-italic"
              style={{ fontSize: 16, color: "var(--muted)", marginBottom: 28, letterSpacing: "0.01em" }}
            >
              Private. Only you ever see this.
            </div>

            <button
              onClick={handleReframe}
              disabled={!thought.trim() || loading}
              className="btn-underline"
              data-testid="reframe-btn"
            >
              {loading ? "Reframing…" : "Reframe"}
            </button>

            {error && error !== "paywall" && (
              <div className="error-msg" data-testid="reframe-error" style={{ marginTop: 28 }}>{error}</div>
            )}
          </div>
        )}

        {/* PAYWALL VIEW */}
        {showPaywall && !reframe && (
          <div className="fade-in" data-testid="paywall-view">
            <div className="eyebrow" style={{ marginBottom: 20 }}>You've reached today's free reframe</div>
            <h2 className="section">
              Come back tomorrow,<br />or open the gate today.
            </h2>
            <p className="serif-italic" style={{ fontSize: 17, color: "var(--accent)", lineHeight: 1.7, marginBottom: 32 }}>
              Members get unlimited reframes — drop in as many thoughts as you carry, any time of day.
              Cancel any time. No clinical talk. Just the work.
            </p>
            <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
              <button className="btn-primary" onClick={() => navigate("/upgrade")} data-testid="paywall-upgrade-btn">
                See membership
              </button>
              <button className="btn-ghost" onClick={() => setError(null)} data-testid="paywall-dismiss-btn">
                Not today
              </button>
            </div>
          </div>
        )}

        {/* REFRAME VIEW */}
        {reframe && (
          <div className="fade-in-up" data-testid="reframe-view">
            <div className="serif-italic" style={{ fontSize: 17, color: "var(--muted)", letterSpacing: "0.1em", marginBottom: 24 }}>
              The Reframe
            </div>

            <h2
              className="serif-italic"
              data-testid="reframe-heading"
              style={{
                fontWeight: 600, fontSize: 32, lineHeight: 1.25,
                margin: "0 0 32px", color: "var(--ink)",
                WebkitFontSmoothing: "auto", MozOsxFontSmoothing: "auto",
              }}
            >
              {reframe.heading}
            </h2>

            <p
              data-testid="reframe-body"
              style={{
                fontSize: 17, lineHeight: 1.8, color: "var(--accent)",
                margin: "0 0 40px", fontWeight: 600,
                WebkitFontSmoothing: "auto", MozOsxFontSmoothing: "auto",
              }}
            >
              {reframe.body}
            </p>

            {reframe.anchor && (
              <div
                data-testid="reframe-anchor"
                style={{
                  paddingTop: 28,
                  borderTop: "1px solid var(--hairline)",
                  marginBottom: 40,
                }}
              >
                <div className="eyebrow" style={{ marginBottom: 12 }}>Today's anchor</div>
                <div
                  className="serif-italic anchor-line"
                  style={{ fontSize: 22, lineHeight: 1.45 }}
                >
                  {reframe.anchor}
                </div>
              </div>
            )}

            <div
              className="serif-italic"
              style={{ fontSize: 15, fontWeight: 500, color: "#1a1008", marginBottom: 48 }}
            >
              Let Go. Live Free. Love Fully. 🤍
            </div>

            <div style={{ display: "flex", gap: 28, flexWrap: "wrap", alignItems: "center" }}>
              <button onClick={startOver} className="btn-underline" data-testid="another-thought-btn">
                Another thought →
              </button>
              <Link to="/journal" className="btn-ghost" data-testid="goto-journal-link">
                Open journal
              </Link>
            </div>
          </div>
        )}

        <div className="footer-tag">Let Go. Live Free. Love Fully.</div>
      </div>
      <div className="container-narrow" style={{ paddingTop: 0, paddingBottom: 32 }}>
        <SiteFooter showTagline={false} />
      </div>
    </div>
  );
}
